import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Scanner } from '@yudiel/react-qr-scanner';
import LogoWhite from '../assets/LOGO_WHITE.png';
import Footer from '../components/Footer';
import { FiCamera, FiX, FiChevronRight } from 'react-icons/fi';
import { RiSofaLine } from 'react-icons/ri';
import { MdFlashOn, MdFlashOff } from 'react-icons/md';

const BG_01 = "https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/ui_images_backgrounds%2FBG_01.jpg?alt=media&token=9e891581-27bf-4bdd-83cf-a39b44af9324";
const BG_02 = "https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/ui_images_backgrounds%2FBG_02.jpg?alt=media&token=b4394dc2-756e-41e8-9cfd-53c25137835d";
const BG_03 = "https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/ui_images_backgrounds%2FBG_03.jpg?alt=media&token=989909db-898b-4f93-babf-0262e968ddff";
const BG_04 = "https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/ui_images_backgrounds%2FBG_04.jpg?alt=media&token=b895a70e-d345-48b9-aa06-a359057f059a";
const BG_05 = "https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/ui_images_backgrounds%2FBG_05.jpg?alt=media&token=d91d94d3-064a-4efa-8bcf-f37cd4a04714";

const SCANNER_BLUE = '#89c6ff';

export default function Access() {
  const navigate = useNavigate();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [activeGrid, setActiveGrid] = useState(0);
  const [scrollPosition, setScrollPosition] = useState({ x: 0, y: 0 });
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [bgIndex, setBgIndex] = useState(0);
  const [bgPosition, setBgPosition] = useState({ x: 0, y: 0 });
  const [bgScale, setBgScale] = useState(1);
  const [bgOpacity, setBgOpacity] = useState(0);
  const [gridOpacity, setGridOpacity] = useState(1);
  const [pageLoaded, setPageLoaded] = useState(false);
  
  // Scanner modal state
  const [showScannerModal, setShowScannerModal] = useState(false);
  const [showShutterEffect, setShowShutterEffect] = useState(false);
  const [torchEnabled, setTorchEnabled] = useState(false);
  const [scanError, setScanError] = useState(null);
  
  const lastGridChangeTime = useRef(0);
  const bgIntervalRef = useRef(null);
  
  const backgrounds = [BG_01, BG_02, BG_03, BG_04, BG_05];

  useEffect(() => {
    document.title = "Outlne | Request Invite";
  }, []);

  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1224);
      setWindowWidth(width);
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPageLoaded(true);
      setGridOpacity(0);
      setBgOpacity(0);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Background slideshow
  useEffect(() => {
    if (!pageLoaded) return;
    
    backgrounds.forEach(src => {
      const img = new Image();
      img.src = src;
    });

    const changeBackground = () => {
      setBgIndex(prev => (prev + 1) % backgrounds.length);
      const direction = Math.random() > 0.5 ? 1 : -1;
      const axis = Math.random() > 0.5 ? 'x' : 'y';
      
      setBgPosition({ x: 0, y: 0 });
      setBgScale(1.05);
      
      setTimeout(() => {
        setBgPosition({
          x: axis === 'x' ? direction * 5 : 0,
          y: axis === 'y' ? direction * 5 : 0
        });
      }, 10);
    };

    changeBackground();
    bgIntervalRef.current = setInterval(changeBackground, 8000);

    return () => clearInterval(bgIntervalRef.current);
  }, [pageLoaded]);

  // Grid animation
  useEffect(() => {
    if (!pageLoaded) return;

    let animationFrame;
    let lastTime = 0;
    const scrollSpeed = 10;

    const animateScroll = (time) => {
      if (!lastTime) lastTime = time;
      const delta = (time - lastTime) / 1000;
      lastTime = time;
      
      const timeSinceChange = time - lastGridChangeTime.current;
      
      if (timeSinceChange > 100) {
        setScrollPosition(prev => ({
          x: (prev.x + scrollSpeed * delta) % 80,
          y: (prev.y + scrollSpeed * delta) % 80
        }));
      }
      
      animationFrame = requestAnimationFrame(animateScroll);
    };

    animationFrame = requestAnimationFrame(animateScroll);

    const interval = setInterval(() => {
      lastGridChangeTime.current = performance.now();
      setScrollPosition({ x: 0, y: 0 });
      setActiveGrid(prev => (prev + 1) % 3);
    }, 4000);

    return () => {
      cancelAnimationFrame(animationFrame);
      clearInterval(interval);
    };
  }, [pageLoaded]);

  // Validate and handle QR code scan
  const handleSuccessfulScan = (detectedCodes) => {
    if (!detectedCodes || detectedCodes.length === 0) return;
    
    const scannedValue = detectedCodes[0].rawValue;
    if (!scannedValue) return;
    
    // Show shutter effect
    setShowShutterEffect(true);
    setTimeout(() => setShowShutterEffect(false), 300);
    
    // Validate the scanned URL
    try {
      let url;
      try {
        url = new URL(scannedValue);
      } catch (e) {
        setScanError('QR code unrecognized');
        setTimeout(() => setScanError(null), 2000);
        return;
      }
      
      const hostname = url.hostname;
      
      // Check if it's from outlne.com
      if (hostname === 'www.outlne.com' || hostname === 'outlne.com') {
        setScanError('Invite code is not active');
        setTimeout(() => setScanError(null), 2000);
        return;
      }
      
      // For other domains
      setScanError('QR code unrecognized');
      setTimeout(() => setScanError(null), 2000);
      
    } catch (error) {
      console.error('QR validation error:', error);
      setScanError('QR code unrecognized');
      setTimeout(() => setScanError(null), 2000);
    }
  };

  const handleScanError = (error) => {
    console.error('Scanner error:', error);
  };

  const handleOpenScanner = () => {
    setShowScannerModal(true);
    setTorchEnabled(false);
    setScanError(null);
  };

  const handleCloseScanner = () => {
    setShowScannerModal(false);
    setTorchEnabled(false);
    setScanError(null);
  };

  const gridStyles = [
    {
      transform: `translate(-${scrollPosition.x}px, -${scrollPosition.y}px)`,
      backgroundSize: '40px 40px',
      display: activeGrid === 0 ? 'block' : 'none',
      opacity: gridOpacity,
      transition: 'opacity 1s ease'
    },
    {
      transform: `translate(-${scrollPosition.x}px, -${scrollPosition.y}px) skewX(-15deg)`,
      backgroundSize: '50px 40px',
      backgroundPositionX: '20px',
      display: activeGrid === 1 ? 'block' : 'none',
      opacity: gridOpacity,
      transition: 'opacity 1s ease'
    },
    {
      transform: `translate(-${scrollPosition.x}px, -${scrollPosition.y}px) perspective(500px) rotateX(15deg)`,
      backgroundSize: '40px 30px',
      backgroundPositionY: '10px',
      display: activeGrid === 2 ? 'block' : 'none',
      opacity: gridOpacity,
      transition: 'opacity 1s ease'
    }
  ];

  return (
    <>
      {/* Fixed Background Layer */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(29, 29, 29, 0.8)',
        zIndex: -3,
      }} />

      {/* Background Slideshow */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: -2,
        overflow: 'hidden',
        opacity: bgOpacity,
        transition: 'opacity 1s ease'
      }}>
        <div style={{
          position: 'absolute',
          top: '-10%',
          left: '-10%',
          right: '-10%',
          bottom: '-10%',
          backgroundImage: `url(${backgrounds[bgIndex]})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          transform: `scale(${bgScale}) translate(${bgPosition.x}%, ${bgPosition.y}%)`,
          mixBlendMode: 'soft-light',
          transition: 'background-image 1s ease-in-out, transform 8s linear',
        }} />
      </div>

      {/* Grid Container */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: isMobile ? '100vw' : '50vw',
        bottom: 0,
        zIndex: -1,
        overflow: 'hidden',
        opacity: gridOpacity,
        transition: 'opacity 1s ease'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: isMobile ? '100vw' : '200vw',
          maskImage: isMobile ? 'none' : 'linear-gradient(to right, transparent 0%, white 5%, white 95%, transparent 100%)',
          WebkitMaskImage: isMobile ? 'none' : 'linear-gradient(to right, transparent 0%, white 5%, white 95%, transparent 100%)',
        }}>
          {gridStyles.map((style, index) => (
            <div key={index} style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: `
                linear-gradient(to right, white 1px, transparent 1px),
                linear-gradient(to bottom, white 1px, transparent 1px)
              `,
              ...style
            }} />
          ))}
        </div>
      </div>

      {/* Desktop Header */}
      {!isMobile && (
        <>
          <div 
            style={{
              position: 'fixed',
              top: '30px',
              left: '30px',
              zIndex: 20,
              cursor: 'pointer',
              fontSize: '2rem',
              color: 'white',
            }}
            onClick={() => navigate('/')}
          >
            ×
          </div>

          <div style={{
            position: 'fixed',
            top: '40px',
            right: '40px',
            zIndex: 10,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            textAlign: 'left',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <img 
                src={LogoWhite} 
                alt="Outlne Logo"
                style={{
                  width: '40px',
                  height: 'auto',
                }}
              />
              <h1 style={{
                fontSize: '1.8rem',
                fontFamily: 'avenir-lt-pro, sans-serif',
                letterSpacing: '-1.2px',
                margin: 0,
                color: 'white',
                fontWeight: 500,
              }}>
                Outlne
              </h1>
            </div>
            <p style={{
              fontSize: '1rem',
              margin: '5px 0 0 0',
              color: 'white',
              fontFamily: 'avenir-lt-pro, sans-serif',
              fontWeight: '500'
            }}>
              Interior Design <span style={{
                backgroundColor: '#ffffff',
                color: 'black',
                padding: '2px 8px',
                borderRadius: '4px',
                display: 'inline-block',
                minWidth: '60px',
                textAlign: 'center'
              }}>
                Editor
              </span>
            </p>
          </div>
        </>
      )}

      {/* Mobile Header */}
      {isMobile && (
        <>
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            height: '110px',
            backgroundColor: 'rgba(31, 31, 31, 0.8)',
            backdropFilter: 'blur(10px)',
            zIndex: 15,
          }} />
          
          <div style={{
            position: 'fixed',
            top: '20px',
            left: '20px',
            zIndex: 20,
            textAlign: 'left',
          }}>
            <h1 style={{
              fontSize: '1.5rem',
              fontFamily: 'avenir-lt-pro, sans-serif',
              letterSpacing: '-1.2px',
              margin: 0,
              color: 'white',
              fontWeight: 500,
            }}>
              Outlne
            </h1>
            <p style={{
              fontSize: '0.9rem',
              margin: '5px 0 0 0',
              color: 'white',
              fontFamily: 'avenir-lt-pro, sans-serif',
              fontWeight: '500'
            }}>
              Interior Design <span style={{
                backgroundColor: '#ffffff',
                color: 'black',
                padding: '2px 6px',
                borderRadius: '4px',
                display: 'inline-block',
                minWidth: '50px',
                textAlign: 'center'
              }}>
                Editor
              </span>
            </p>
          </div>

          <div style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 20,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: '10px',
          }}>
            <img 
              src={LogoWhite} 
              alt="Outlne Logo"
              style={{
                width: '40px',
                height: '40px',
              }}
            />
            <div 
              style={{
                cursor: 'pointer',
                marginRight: '10px',
                fontSize: '1.8rem',
                color: 'white',
                marginTop: '-5px',
              }}
              onClick={() => navigate('/')}
            >
              ×
            </div>
          </div>
        </>
      )}

      {/* Main Content */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 5,
        overflowY: 'auto',
        overflowX: 'hidden',
        opacity: pageLoaded ? 1 : 0,
        transition: 'opacity 1s ease',
        overscrollBehavior: 'none'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          minHeight: '100vh',
          paddingTop: isMobile ? '135px' : isTablet ? '80px' : '80px',
          paddingBottom: '0',
          position: 'relative',
          zIndex: 5,
        }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            maxWidth: isMobile ? '100%' : isTablet ? '600px' : '500px',
            padding: isMobile ? '0 20px' : '0 40px',
            flex: '1',
          }}>
            {/* Header */}
            <div style={{
              textAlign: 'center',
              maxWidth: '600px',
              marginBottom: isMobile ? '2rem' : isTablet ? '2.5rem' : '3rem',
              width: '100%',
            }}>
              <h1 style={{
                fontFamily: 'avenir-lt-pro, sans-serif',
                fontSize: isMobile ? '1.6rem' : isTablet ? '2rem' : '2.2rem',
                fontWeight: '500',
                color: 'white',
                marginBottom: '0.8rem',
                lineHeight: '1.2',
                letterSpacing: '-0.5px'
              }}>
                Received an Invite?
              </h1>
              <p style={{
                color: '#ccc',
                fontSize: isMobile ? '0.9rem' : isTablet ? '1rem' : '1.1rem',
                lineHeight: '1.5',
                fontFamily: 'avenir-lt-pro, sans-serif',
                margin: 0
              }}>
                Scan your physical invite card<br></br> or sign up for early access
              </p>
            </div>

            {/* Scan Card */}
            <div style={{
              backgroundColor: 'rgba(47, 47, 47, 0.9)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '1.5rem',
              padding: isMobile ? '2rem' : '2.5rem',
              textAlign: 'center',
              backdropFilter: 'blur(20px)',
              width: '100%',
              maxWidth: '400px',
              marginBottom: '2rem',
              transition: 'all 0.3s ease',
              cursor: 'pointer',
            }}
            onClick={handleOpenScanner}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(47, 47, 47, 0.95)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.4)';
              e.currentTarget.style.transform = 'translateY(-4px)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.15)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}>
            <div style={{
              width: '80px',
              height: '80px',
              margin: '0 auto 1.5rem auto',
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <FiCamera size={40} color="#ffffff" />
            </div>
            <h2 style={{
              fontFamily: 'avenir-lt-pro, sans-serif',
              fontSize: isMobile ? '1.3rem' : '1.5rem',
              fontWeight: '500',
              color: 'white',
              marginBottom: '0.75rem',
            }}>
              Scan Invite
            </h2>
            <p style={{
              color: '#eee',
              fontSize: isMobile ? '0.85rem' : '0.9rem',
              lineHeight: '1.5',
              marginBottom: '1rem',
            }}>
              Use your camera to scan the QR code on your physical invite card
            </p>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              color: '#ffffff',
              fontSize: '0.9rem',
              marginTop: '0.5rem',
            }}>
              <span>Click to open scanner</span>
              <FiChevronRight size={16} />
            </div>
          </div>

          {/* Divider */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            marginBottom: '2rem',
            width: '100%',
            maxWidth: '300px',
          }}>
            <div style={{
              flex: 1,
              height: '1px',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
            }} />
            <span style={{ color: '#aaa', fontSize: '0.85rem' }}>OR</span>
            <div style={{
              flex: 1,
              height: '1px',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
            }} />
          </div>

          {/* Request Invite Card */}
          <div 
            style={{
              backgroundColor: 'rgba(47, 47, 47, 0.9)',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              borderRadius: '1rem',
              padding: isMobile ? '1.5rem' : '2rem',
              textAlign: 'center',
              backdropFilter: 'blur(20px)',
              width: '100%',
              maxWidth: '400px',
              transition: 'all 0.3s ease',
              cursor: 'pointer',
            }}
            onClick={() => {
              const width = 500;
              const height = 700;
              const left = window.screen.width / 2 - width / 2;
              const top = window.screen.height / 2 - height / 2;
              window.open('/auth', 'Outlne Sign Up', `width=${width},height=${height},left=${left},top=${top}`);
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(47, 47, 47, 0.95)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
              e.currentTarget.style.transform = 'translateY(-4px)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.15)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}>
            <div style={{
              width: '60px',
              height: '60px',
              margin: '0 auto 1.5rem auto',
              backgroundColor: 'rgba(255, 255, 255, 0.09)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <RiSofaLine size={30} color="#ffffff" />
            </div>
            <h2 style={{
              fontFamily: 'avenir-lt-pro, sans-serif',
              fontSize: isMobile ? '1.3rem' : '1.5rem',
              fontWeight: '500',
              color: 'white',
              marginBottom: '0.75rem',
            }}>
              Invite Me
            </h2>
            <p style={{
              color: '#ddd',
              fontSize: isMobile ? '0.85rem' : '0.9rem',
              lineHeight: '1.5',
              marginBottom: '0',
            }}>
              Sign up for early access and join the waitlist.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          width: '100%',
          position: 'relative',
          zIndex: 5,
          backgroundColor: '#1a1a1a',
          marginTop: isMobile ? '3rem' : '4rem',
        }}>
          <Footer />
        </div>
      </div>
    </div>

      {/* Scanner Modal */}
      {showScannerModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(29, 29, 29, 0.95)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backdropFilter: 'blur(10px)',
        }}>
          <div style={{
            position: 'relative',
            width: '100%',
            maxWidth: '500px',
            padding: '20px',
          }}>
            <button
              onClick={handleCloseScanner}
              style={{
                position: 'absolute',
                top: '-40px',
                right: '10px',
                background: 'none',
                border: 'none',
                color: 'white',
                fontSize: '28px',
                cursor: 'pointer',
                zIndex: 1001,
                padding: '10px',
              }}
            >
              <FiX size={28} />
            </button>

            <h3 style={{
              fontFamily: 'avenir-lt-pro, sans-serif',
              fontSize: '1.2rem',
              fontWeight: '500',
              color: 'white',
              textAlign: 'center',
              marginBottom: '20px',
            }}>
              Scan Your Invite QR Code
            </h3>

            <div style={{
              position: 'relative',
              width: '100%',
              aspectRatio: '1 / 1',
              backgroundColor: '#000',
              borderRadius: '24px',
              overflow: 'hidden',
              border: `2px solid ${SCANNER_BLUE}`,
            }}>
              <Scanner
                onScan={handleSuccessfulScan}
                onError={handleScanError}
                constraints={{
                  facingMode: 'environment',
                }}
                scanDelay={500}
                components={{
                  finder: false,
                  tracker: false,
                  audio: false,
                }}
                styles={{
                  container: { width: '100%', height: '100%' },
                  video: { width: '100%', height: '100%', objectFit: 'cover' },
                }}
              />

              {/* CUSTOM OVERLAY - ONLY WHAT YOU WANT */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                pointerEvents: 'none',
              }}>
                {/* Darkened edges for focus effect */}
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'radial-gradient(circle, transparent 60%, rgba(0,0,0,0.5) 100%)',
                }} />
                
                {/* Custom corner brackets - blue only */}
                <div style={{
                  position: 'absolute',
                  top: 20,
                  left: 20,
                  width: 40,
                  height: 40,
                  borderTop: `3px solid ${SCANNER_BLUE}`,
                  borderLeft: `3px solid ${SCANNER_BLUE}`,
                }} />
                <div style={{
                  position: 'absolute',
                  top: 20,
                  right: 20,
                  width: 40,
                  height: 40,
                  borderTop: `3px solid ${SCANNER_BLUE}`,
                  borderRight: `3px solid ${SCANNER_BLUE}`,
                }} />
                <div style={{
                  position: 'absolute',
                  bottom: 20,
                  left: 20,
                  width: 40,
                  height: 40,
                  borderBottom: `3px solid ${SCANNER_BLUE}`,
                  borderLeft: `3px solid ${SCANNER_BLUE}`,
                }} />
                <div style={{
                  position: 'absolute',
                  bottom: 20,
                  right: 20,
                  width: 40,
                  height: 40,
                  borderBottom: `3px solid ${SCANNER_BLUE}`,
                  borderRight: `3px solid ${SCANNER_BLUE}`,
                }} />
              </div>

              {/* Shutter Effect */}
              {showShutterEffect && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'white',
                  animation: 'shutterFlash 0.3s ease-out',
                }} />
              )}
            </div>

            {/* Flash button removed - library doesn't support programmatic torch control */}

            {/* Error Message */}
            {scanError && (
              <div style={{
                marginTop: '16px',
                padding: '12px',
                borderRadius: '12px',
                backgroundColor: 'rgba(255, 68, 68, 0.2)',
                border: '1px solid #ff4444',
                textAlign: 'center',
              }}>
                <p style={{
                  color: '#ff8888',
                  fontSize: '0.9rem',
                  margin: 0,
                }}>
                  {scanError}
                </p>
              </div>
            )}

            {/* Simple instruction text */}
            <p style={{
              color: '#aaa',
              fontSize: '0.8rem',
              textAlign: 'center',
              marginTop: '20px',
            }}>
              Align QR code within the frame
            </p>
          </div>
        </div>
      )}

      <style>{`
        @keyframes shutterFlash {
          0% { opacity: 1; }
          100% { opacity: 0; }
        }
        html, body, #root {
          overflow: hidden;
          height: 100%;
          width: 100%;
          position: fixed;
          margin: 0;
          padding: 0;
        }
        * {
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
          -webkit-tap-highlight-color: transparent;
        }
      `}</style>
    </>
  );
}